import axios from "axios";

// Required so that the back end can identify requests that are asking for JSON.
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * Throw a given exception if a value evaluates to false
 *
 * @param {any}    value
 * @param {string} message
 *
 * @throws Throws an error if the value is falsey
 * @returns {any}
 */
const throwIfNot = (value, message="No value") => {
    if( value ) return value;
    throw message;
};

/**
 * API Manager Class
 *
 * This class is responsible for making and managing API calls to the back-end,
 * when adding API methods, make sure to return a response that's compatible with
 * the process of retrieving validation (and other forms of) errors.
 */
class ApiManager {

    /**
     * @typedef {Object} AuthenticateResponse
     *
     * @property {boolean} authenticated
     * @property {string}  next
     */

    /**
     * Authenticate a User by email and password
     *
     * @param {string} email
     * @param {string} password
     * @returns {Promise<AuthenticateResponse>}
     */
    authenticateUser( email, password )
    {
        return this._post( '/login', { email: email, password : password })
            .then( response => throwIfNot(response.data) )
            .catch( error => {
                // If we get here, then we got a non-200 response, so we need to manually check
                // for a 422, which is what laravel will send if the request does not authenticate.
                // Why don't we just return a 200 with an 'authenticated: false' field? Because we don't
                // want browsers and password managers to mistake the 200 as a successful login, we
                // want to be sure it will be recognised for what it is.
                if( error.response && error.response.status === 422 )
                    return { authenticated: false };
                throw 'unknown'; // trigger external error handlers.
            })
        ;
    }

    /**
     * @typedef {Object} CreateCustomer
     *
     * @property {number} location
     * @property {string} firstname
     * @property {string} surname
     * @property {string} email
     * @property {string} phone
     * @property {string} apartment
     * @property {string} password
     * @property {string} password_confirm
     */

    /**
     * @param {CreateCustomer} payload
     */
    createCustomerForOrderForm( payload ) {
        return this._post( '/place-order/signup', payload )
            .then( response => ({ result: true, customer: response.data.customer }))
            .catch( error => {
                const response = error.response || {};
                if( response.status === 422 ) {
                    return { result: false, errors: response.data || {} };
                }

                throw error;
            })
        ;
    }

    /**
     * @typedef {Object} Location
     *
     * @property {number}   location_id
     * @property {string}   name
     * @property {string}   postcode
     * @property {boolean}  concierge
     * @property {string[]} address
     * @property {number[]} coords
     * @property {number[]} lockers
     * @property {Object}   deadlines
     * @property {string}   instruction
     * @property {string}   image_url
     */

    /**
     * Search for a location by postcode
     *
     * @param postcode
     * @returns {Promise<Location>}
     */
    getLocationForPostcode( postcode )
    {
        postcode = postcode.trim();

        return this._get(`/find-location?postcode=${postcode}`)
            .then( response => this._checkResponseContent(response) )
            .then( response => response.location )
        ;
    }

    talkToAnExpert( name, email, phone, enquiry )
    {
        let message_params = {
            name    : name,
            email   : email,
            phone   : phone,
            enquiry : enquiry
        };

        return this._post( '/talk-to-an-expert', message_params )
            .then( response => response.data )
            .catch( error => {
                const response = error.response;
                if( response.status === 422 ) {
                    return { sent: false, errors: response.data || {} };
                }

                throw error;
            })
        ;
    }

    sendPasswordResetEmail( email )
    {
        return this._post('/reset-password', { email: email })
            //.then( response => console.log('response', response ))
        ;
    }

    /**
     * @typedef {Object} SubmitOrder
     *
     * @property {number}  customer
     * @property {number}  locker
     * @property {number}  location
     * @property {boolean} concierge
     * @property {number}  service
     * @property {string}  instructions
     * @property {string}  voucher
     * @property {boolean} check_tc
     */

    /**
     *
     * @param {SubmitOrder} order
     */
    submitOrder( order )
    {
        return this._post( '/place-order/submit', order )
            .then( response => ({
                result: true,
                next: response.data.next.replace( window.location.origin, '' )
            }))
            .catch( error => {
                const response = error.response;
                if( response.status === 422 ) {
                    return { result: false, errors: response.data || {} };
                }

                throw error;
            })
        ;
    }

    /**
     * Check the content of the response data for an error field
     *
     * todo: why is this async? Is async still needed?
     *
     * @param {Object} response
     * @returns {Promise<Object>}
     * @private
     */
    async _checkResponseContent( response )
    {
        if( response.data && response.data.error )
            throw( response.data.error );

        if( response.status !== 200 ) {
            console.warn('Non-200 response', response);
            throw { error: "An unknown error occurred" };
        }

        return response.data;
    }

    /**
     * Wrapper for axios get methods
     *
     * @param {string} url
     * @returns {Promise<Object>}
     * @private
     */
    _get( url ) {
        return axios.get( url );
    }


    /**
     * Wrapper for axios post methods
     *
     * @param {string} url
     * @param {Object} data
     * @param {Object} overrides
     * @returns {Promise<Object>}
     * @private
     */
    _post( url, data={}, overrides={} )
    {
        return axios.post( url, data );
    }
}

/**
 * ApiManager Instance
 *
 * We shouldn't need more than one instance of the API Manager, so whilst the class
 * does not implement the singleton pattern, it is in effect a singleton as the only
 * export of this module.
 *
 * @type {ApiManager}
 */
const apiService = new ApiManager();
export default apiService;
